/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { MemberTypeForm, ProgressButton } from 'components';
import options from 'config/member_types.config';
import { useState } from 'react';
import { useAddMemberTypeMutation, useEditMemberTypeMutation } from 'store/api/version-api.slice';

const MemberTypeDialog = ({ onClose, open, member }) => {
	const [formValues, setFormValues] = useState({
		member_type: member ? member.member_type : '',
		billing: member ? member.billing : options.billing[0].db_key,
		renewal: member ? member.renewal : options.renewal[1].db_key,
		amount: member ? member.amount : 25,
		allow_linked: member ? member.allow_linked : false,
		linked_limit: member ? member.linked_limit : 1,
		visible: member ? member.visible : true,
		free_type: member ? member.free_type : false,
		directory_list: member ? member.directory_list : false
	});

	const [editMemberType, { isLoading: isEditLoading }] = useEditMemberTypeMutation();
	const [addMemberType, { isLoading: isAddLoading }] = useAddMemberTypeMutation();

	const handleChange = (prop, value) => {
		setFormValues(prev => ({
			...prev,
			[prop]: value
		}));
	};

	const resetForm = () => {
		setFormValues({
			member_type: '',
			billing: options.billing[0].db_key,
			renewal: options.renewal[1].db_key,
			amount: 25,
			allow_linked: false,
			link_limit: 0,
			visible: true,
			free_type: false,
			directory_list: false
		});
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleSave = async () => {
		const values = { ...formValues };
		if (member) {
			await editMemberType({ id: member.id, payload: values });
		} else {
			await addMemberType(values);
		}
		handleClose();
	};

	const title = member ? 'Edit Member Type' : 'Add Member Type';
	return (
		<Dialog
			maxWidth={'md'}
			fullWidth={true}
			open={open}
			onClose={handleClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">{title}</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					<MemberTypeForm
						linked_limit={formValues.linked_limit}
						allow_linked={formValues.allow_linked}
						member_type={formValues.member_type}
						amount={formValues.amount}
						renewal={formValues.renewal}
						billing={formValues.billing}
						visible={formValues.visible}
						free_type={formValues.free_type}
						directory_list={formValues.directory_list}
						handlePropChange={handleChange}
						onSave={handleSave}
						onCancel={onClose}
					/>
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" color="inherit" onClick={handleClose}>
					Cancel
				</Button>
				<ProgressButton
					circleColor="secondary"
					button={
						<Button
							disabled={formValues.member_type.length === 0}
							onClick={handleSave}
							variant="contained"
							color="primary"
							autoFocus
						>
							Save
						</Button>
					}
					isLoading={isAddLoading || isEditLoading}
				/>
			</DialogActions>
		</Dialog>
	);
};
export default MemberTypeDialog;
